<template>
  <div class="main" :class="{ 'keyboard-open': isKeyboardOpen }">
    <HeaderBanner
      class="w-100"
      v-if="showBanner"
      :headerBannerImage="useAsset('img/credit-line/cc_data_banner.png')"
      :headerBannerTitle="t('PAYJOY_CARD_VIEWS.COMPLETE_DATA_TITLE')"
      :headerBannerSubtitle="t('PAYJOY_CARD_VIEWS.COMPLETE_DATA_SUBTITLE')"
    />
    <div class="container pt-3 pb-0">
      <img
        :src="getIcon('img/credit-line/numeric_keypad.png')"
        alt="icon"
        class="mt-5 w-25"
      />

      <div class="text-center">
        <p class="title-style">
          {{ title }}
        </p>
        <p class="description-style" v-html="description"></p>
      </div>
    </div>
    <div class="d-flex mb-3" ref="inputContainer">
      <div class="p-3 w-100">
        <TextInputEnhance
          :label="t('PAYJOY_CARD_VIEWS.USER_FORM_PHONE_NUMBER')"
          :fill="true"
          :max-length="10"
          :type="'text'"
          :inputMode="'numeric'"
          v-model:value="phoneNumberField"
          :error="phoneNumberFieldError !== ''"
          :errorMessage="phoneNumberFieldError"
          :errorIcon="false"
          :prefix="prefix"
          :highlight="highlighted"
          @onFocus="handleFocus"
          @onBlur="handleBlur"
        />
      </div>
    </div>

    <div class="floating-btn-placeholder" />
    <div class="floating-btn">
      <Button
        id="btn-continue"
        @click="onConfirmCick"
        :disabled="isDisabled"
        :loading="props.loading"
      >
        {{ t("CL_TRANSACTIONS.CONFIRM_NUMBER") }}
      </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
	ref,
	watch,
	onMounted,
	onUnmounted,
	nextTick,
	inject,
	computed,
} from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import Button from "@/apps/credit-line/components/Button.vue";
import TextInputEnhance from "@/shared-components/consumer/TextInputEnhance.vue";
import type { ICreditCardUserFormData } from "@/apps/credit-line/types/forms";
import AnalyticEvents from "@/shared-helpers/google-analytics-events.helper";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import HeaderBanner from "@/apps/credit-line/components/HeaderBanner.vue";

const props = defineProps<{
	preFilledData?: ICreditCardUserFormData | null;
	showBanner?: boolean;
	disableButton?: boolean;
	loading?: boolean;
	title?: string;
	description?: string;
}>();

const emit =
	defineEmits<(e: "onContinueClick", data: ICreditCardUserFormData) => void>();

const { t } = useI18n();
const startedFillout = ref(false);
const isKeyboardOpen = ref(false);
const inputContainer = ref<HTMLElement | null>(null);

// Form fields
const firstNameField = ref<string>(props.preFilledData?.firstName as string);
const lastNameField = ref<string>(props.preFilledData?.lastName as string);
const identificationField = ref<string>(
	props.preFilledData?.identificationValue || ""
);
const genderField = ref<string>(props.preFilledData?.gender || "");
const birthdayField = ref<string>(props.preFilledData?.birthday || "");
const phoneNumberField = ref<string>(props.preFilledData?.phoneNumber || "");
const phoneNumberFieldError = ref<string>("");
const highlighted = ref<boolean>(false);

const prefix = inject("prefix");

// Computed property to disable the button when there are not exactly 10 digits
const isDisabled = computed(() => {
	// If the phone number does not have 10 digits, the button is disabled
	return phoneNumberField.value.length !== 10;
});

watch(phoneNumberField, (newVal) => {
	if (newVal !== "" && !startedFillout.value) {
		startedFillout.value = true;
		logAnalyticsEvent(
			AnalyticEvents.CreditLine.CcUserPhoneConfirmationStartFillout,
			"CC_Details"
		);
	}
});

onMounted(() => {
	logAnalyticsEvent(
		AnalyticEvents.CreditLine.CcUserPhoneConfirmationFormLoaded,
		"CC_Details",
		undefined,
		{ ...props.preFilledData }
	);

	// Detect keyboard open/close by listening to window resize events
	const handleResize = () => {
		// If the viewport is significantly reduced, we assume the keyboard is open
		isKeyboardOpen.value = window.innerHeight < screen.height * 0.7;
	};

	if (phoneNumberField.value.length > 10) {
		phoneNumberField.value = phoneNumberField.value.slice(-10);
	}

	window.addEventListener("resize", handleResize);

	// Cleanup
	onUnmounted(() => {
		window.removeEventListener("resize", handleResize);
	});
});

const handleFocus = async () => {
	await nextTick();
	// Scroll to input when it receives focus
	setTimeout(() => {
		inputContainer.value?.scrollIntoView({
			behavior: "smooth",
			block: "center",
		});
	}, 300);
};

const handleBlur = () => {
	const isNumberValid = checkPhoneNumber(phoneNumberField.value);

	if (phoneNumberField.value !== "" && !isNumberValid) {
		phoneNumberFieldError.value = t("PAYJOY_CARD_VIEWS.PHONE_NUMBER_ERROR");
	} else {
		phoneNumberFieldError.value = "";
	}
};

const getIcon = (path: string): string => {
	return useAsset(path);
};

const checkPhoneNumber = (phone: string): boolean => {
	const phoneRegex = /^\d{10}$/;
	return phoneRegex.test(phone);
};

watch(phoneNumberField, () => {
	if (checkPhoneNumber(phoneNumberField.value)) {
		phoneNumberFieldError.value = "";
	}
});

const onConfirmCick = () => {
	const requriedFieldError = t("PAYJOY_CARD_VIEWS.FORM_REQUIRED_FIELD");
	phoneNumberFieldError.value =
		phoneNumberField.value === "" ? requriedFieldError : "";

	if (!checkPhoneNumber(phoneNumberField.value)) {
		phoneNumberFieldError.value = t("PAYJOY_CARD_VIEWS.PHONE_NUMBER_ERROR");
	}

	if (!phoneNumberFieldError.value) {
		const data: ICreditCardUserFormData = {
			firstName: firstNameField.value,
			lastName: lastNameField.value,
			birthday: birthdayField.value,
			identificationValue: identificationField.value,
			taxIdentificationValue: identificationField.value,
			email: props.preFilledData?.email,
			gender: genderField.value,
			phoneNumber: phoneNumberField.value,
		};
		logAnalyticsEvent(
			AnalyticEvents.CreditLine.CcUserPhoneConfirmationContinueClick,
			"CC_Details"
		);
		emit("onContinueClick", data);
	}
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";

.main {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;

  &.keyboard-open {
    min-height: auto;
    padding-bottom: 100px;
  }
}

.container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 32px;
  flex: 1 0 0;
  align-self: stretch;
  font-family: Inter, serif;
}
.field {
  width: 60%;
  margin-right: 5px;
}
.field-attachment {
  width: 60%;
}
.title {
  font-size: 16px;
}

.title-style {
  width: 100%;
  position: relative;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  font-family: Inter;
  color: #000;
  text-align: center;
  display: inline-block;
}

.description-style {
  width: 100%;
  min-height: 81px;
  position: relative;
  font-size: 14px;
  line-height: 21px;
  font-weight: 500;
  font-family: Inter;
  color: #595a5a;
  text-align: center;
  display: inline-block;
}

.floating-btn-placeholder,
.floating-btn {
  height: 80px;
  width: 100%;
}
.floating-btn {
  background-color: white;
  position: fixed;
  padding: 10px 20px;
  left: 0;
  bottom: 0;
  z-index: 200;
}
.error-label {
  color: $pj-color-red;
  font-size: 14px;
}
</style>
